import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type SxProps, type Theme } from '@mui/material/styles';

/**
 * StatValue — the "bold number" of the Version D redesign.
 *
 * A large, heavy, tightly-tracked figure with an optional de-emphasised
 * unit. This is the treatment that makes KPIs read from across the room
 * while the rest of the card stays calm. Used by MetricCard and anywhere
 * a headline metric is shown.
 */
export interface StatValueProps {
  /** The figure itself, e.g. "1,240", "32.4", "11", "Low". */
  value: React.ReactNode;
  /** Optional trailing unit, rendered smaller and muted, e.g. "°", "m", "%". */
  unit?: React.ReactNode;
  /** Font size of the value in px. Defaults to the KPI size (40). */
  size?: number;
  color?: string;
  sx?: SxProps<Theme>;
}

export function StatValue({ value, unit, size = 40, color, sx }: StatValueProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', ...sx }}>
      <Typography
        component="span"
        sx={{
          fontSize: `${size}px`,
          fontWeight: 850,
          letterSpacing: '-0.04em',
          lineHeight: 1,
          color: color ?? 'text.primary',
        }}
      >
        {value}
      </Typography>
      {unit != null && (
        <Typography
          component="span"
          sx={{
            ml: 0.5,
            fontSize: `${Math.round(size * 0.42)}px`,
            fontWeight: 750,
            color: 'text.secondary',
          }}
        >
          {unit}
        </Typography>
      )}
    </Box>
  );
}
